/**
 * statIcons — textures for the HUD's stat indicator row.
 *
 * The three glyphs live as SVGs under assets/art/ui/hud/icons/stats/ so the art
 * can be replaced without touching layout code. Loading is fire-and-forget:
 * get() returns undefined until the texture lands, and MatchStats keeps its
 * sprite hidden until then — the numbers are readable on their own meanwhile.
 *
 * Textures are cached per-URL for the page's lifetime. Nothing here is
 * per-match, so there is no invalidation path; PixiHUDRenderer.reset() rebuilds
 * the sprites but reuses these textures.
 */
import * as PIXI from 'pixi.js';
import { pixiHudLogger } from './logger.js';

const BASE = 'assets/art/ui/hud/icons/stats';

export const STAT_ICONS = {
  eliminations: `${BASE}/eliminations.svg`,
  deaths: `${BASE}/deaths.svg`,
  players: `${BASE}/players.svg`
};

const textures = new Map();
const inFlight = new Map();

/** The loaded texture for a STAT_ICONS key, or undefined if it isn't ready. */
export function get(name) {
  return textures.get(name);
}

/**
 * Start loading every stat icon. Safe to call more than once — each URL is
 * fetched at most once. A failed icon is logged and simply stays absent.
 */
export function preload() {
  for (const [name, url] of Object.entries(STAT_ICONS)) {
    if (textures.has(name) || inFlight.has(name)) continue;
    const task = PIXI.Assets.load(url)
      .then((texture) => {
        textures.set(name, texture);
        return texture;
      })
      .catch((err) => {
        pixiHudLogger.warn(`[statIcons] Failed to load ${url}`, err);
      })
      .finally(() => {
        inFlight.delete(name);
      });
    inFlight.set(name, task);
  }
  return Promise.all(inFlight.values());
}
